import * as React from 'react';

import {
  enumSalesLoadLineItemPhase,
  enumUnit,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { TouchButton } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideAsterisk, LucidePackage } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { CatalyticConverterModal } from '../../_shared/CatalyticConverterModal';
import {
  InventoryItemsModal,
  InventoryTagData,
} from '../../_shared/InventoryItemsModal';

import { $inventoryItemsById } from '~/modules/trade/_state/loadForm.state';
import {
  $autoFocusRowId,
  $catalyticModalState,
  $closeCatalyticModal,
  $closeMaterialsModal,
  $loadHeaderRect,
  $materialsModalState,
  $openCatalyticModal,
  $openMaterialsModal,
} from '~/modules/trade/_state/loadStaged.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadFormLineItem } from '~/modules/trade/types/load';
import { createDefaultWeights } from '~/modules/trade/utils/weight';
import { useUIPreferences } from '~/shared/hooks';

type MaterialsWidgetProps = {
  onAddItem?: (
    type: 'Raw' | 'Tagged',
    data?: Partial<LoadFormLineItem>,
  ) => void;
  onUpdateItem?: (id: string, updates: Partial<LoadFormLineItem>) => void;
  highlightedMaterialIds?: string[];
  highlightedSectionTitle?: string;
};

const MODAL_SPACING = 20;

const FALLBACK_MODAL_STYLE: React.CSSProperties = {
  position: 'fixed',
  left: MODAL_SPACING,
  top: MODAL_SPACING,
  height: `calc(100vh - ${MODAL_SPACING * 2}px)`,
  width: `calc(100vw - ${MODAL_SPACING * 2}px)`,
};

const calculateModalStyle = (headerRect: {
  width: number;
  height: number;
  left: number;
  top: number;
}): React.CSSProperties => ({
  position: 'fixed',
  left: headerRect.left + MODAL_SPACING,
  width: headerRect.width - MODAL_SPACING * 2,
  top: `calc(${headerRect.height + headerRect.top}px + ${MODAL_SPACING}px)`,
  height: `calc(100vh - ${headerRect.height + headerRect.top}px - ${MODAL_SPACING}px)`,
});

type MaterialButtonProps = {
  onPress: () => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
};

const MaterialButton = ({ onPress, icon, children }: MaterialButtonProps) => (
  <TouchButton
    onPress={onPress}
    className="@MaterialButton w-full"
    isSelected={false}
    leftElement={icon}
    color="primary"
    variant="outline"
  >
    {children}
  </TouchButton>
);

export const MaterialsWidget = ({
  onAddItem,
  onUpdateItem,
  highlightedMaterialIds,
  highlightedSectionTitle,
}: MaterialsWidgetProps) => {
  const t = useTranslations();
  const { touchModeEnabled } = useUIPreferences();

  const loadHeaderRect = useAtomValue($loadHeaderRect);
  const setAutoFocusRowId = useSetAtom($autoFocusRowId);
  const catalyticModalState = useAtomValue($catalyticModalState);
  const openCatalyticModal = useSetAtom($openCatalyticModal);
  const closeCatalyticModal = useSetAtom($closeCatalyticModal);
  const materialsModalState = useAtomValue($materialsModalState);
  const openMaterialsModal = useSetAtom($openMaterialsModal);
  const closeMaterialsModal = useSetAtom($closeMaterialsModal);
  const inventoryItemsById = useAtomValue($inventoryItemsById);

  const modalStyle = React.useMemo<React.CSSProperties>(() => {
    if (loadHeaderRect.width && loadHeaderRect.height) {
      return calculateModalStyle(loadHeaderRect);
    }
    return FALLBACK_MODAL_STYLE;
  }, [loadHeaderRect]);

  const isChangeMode = materialsModalState.mode === 'change';

  const openItemsModal = React.useCallback(() => {
    openMaterialsModal({ mode: 'add', itemType: 'items' });
  }, [openMaterialsModal]);

  const openTagsModal = React.useCallback(() => {
    openMaterialsModal({ mode: 'add', itemType: 'tags' });
  }, [openMaterialsModal]);

  const handleOpenCatalyticModal = React.useCallback(() => {
    openCatalyticModal({ mode: 'add', selectedItemId: null, lineItemId: null });
  }, [openCatalyticModal]);

  const handleAddRawMaterial = React.useCallback(
    (materialId?: string) => {
      if (!onAddItem) {
        console.warn('onAddItem handler not provided');
        return;
      }

      const newItemId = `temp-${uuidv4()}`;
      const item = materialId ? inventoryItemsById[materialId] : undefined;
      const unit = (item?.effectivePricingUnit as Unit) ?? enumUnit.Pounds;

      onAddItem('Raw', {
        id: newItemId,
        itemId: materialId,
        phase: enumSalesLoadLineItemPhase.Staged,
        weights: createDefaultWeights(unit),
        pricingUnit: unit,
      });

      setAutoFocusRowId(newItemId);
    },
    [onAddItem, setAutoFocusRowId, inventoryItemsById],
  );

  const handleAddTaggedMaterial = React.useCallback(
    (tagId?: string, tagData?: InventoryTagData) => {
      if (!onAddItem) {
        console.warn('onAddItem handler not provided');
        return;
      }

      const newItemId = `temp-${uuidv4()}`;
      const unit =
        (tagData?.item.effectivePricingUnit as Unit) ?? enumUnit.Pounds;

      onAddItem('Tagged', {
        id: newItemId,
        itemId: tagData?.item.id,
        tagId: tagId,
        phase: enumSalesLoadLineItemPhase.Staged,
        weights: createDefaultWeights(unit, {
          gross: tagData?.grossQuantity ?? 0,
          tare: tagData?.tare ?? 0,
          net: tagData?.netQuantity ?? 0,
        }),
        pricingUnit: unit,
      });

      setAutoFocusRowId(newItemId);
    },
    [onAddItem, setAutoFocusRowId],
  );

  const handleMaterialSelect = React.useCallback(
    (materialId: string) => {
      if (isChangeMode && materialsModalState.lineItemId && onUpdateItem) {
        const item = inventoryItemsById[materialId];
        const unit = (item?.effectivePricingUnit as Unit) ?? enumUnit.Pounds;
        onUpdateItem(materialsModalState.lineItemId, {
          itemId: materialId,
          pricingUnit: unit,
        });
        closeMaterialsModal();
        return;
      }

      handleAddRawMaterial(materialId);
    },
    [
      isChangeMode,
      materialsModalState.lineItemId,
      onUpdateItem,
      closeMaterialsModal,
      handleAddRawMaterial,
      inventoryItemsById,
    ],
  );

  const handleTagSelect = React.useCallback(
    (tagId: string, tagData: InventoryTagData) => {
      if (isChangeMode && materialsModalState.lineItemId && onUpdateItem) {
        const unit =
          (tagData.item.effectivePricingUnit as Unit) ?? enumUnit.Pounds;
        onUpdateItem(materialsModalState.lineItemId, {
          tagId,
          itemId: tagData.item.id,
          pricingUnit: unit,
          weights: createDefaultWeights(unit, {
            gross: tagData.grossQuantity ?? 0,
            tare: tagData.tare ?? 0,
            net: tagData.netQuantity ?? 0,
          }),
        });
        closeMaterialsModal();
        return;
      }

      handleAddTaggedMaterial(tagId, tagData);
      closeMaterialsModal();
    },
    [
      isChangeMode,
      materialsModalState.lineItemId,
      onUpdateItem,
      closeMaterialsModal,
      handleAddTaggedMaterial,
    ],
  );

  const handleCatalyticItemSelect = React.useCallback(
    (materialId?: string) => {
      const lineItemId = catalyticModalState.isOpen
        ? catalyticModalState.lineItemId
        : null;
      const item = materialId ? inventoryItemsById[materialId] : undefined;
      const unit = (item?.effectivePricingUnit as Unit) ?? enumUnit.Each;

      if (lineItemId && onUpdateItem) {
        onUpdateItem(lineItemId, { itemId: materialId, pricingUnit: unit });
        return;
      }

      if (!onAddItem) {
        console.warn('onAddItem handler not provided');
        return;
      }

      const newItemId = `temp-${uuidv4()}`;

      onAddItem('Raw', {
        id: newItemId,
        itemId: materialId,
        phase: enumSalesLoadLineItemPhase.Staged,
        weights: createDefaultWeights(unit),
        pricingUnit: unit,
      });

      setAutoFocusRowId(null);
    },
    [
      catalyticModalState.isOpen,
      catalyticModalState.lineItemId,
      onUpdateItem,
      onAddItem,
      setAutoFocusRowId,
      inventoryItemsById,
    ],
  );

  const handleModalOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        closeMaterialsModal();
      }
    },
    [closeMaterialsModal],
  );

  return (
    <>
      <MaterialButton
        onPress={
          touchModeEnabled ? openItemsModal : () => handleAddRawMaterial()
        }
        icon={<LucidePackage />}
      >
        {t.loadStaged.addMaterial}
      </MaterialButton>

      <MaterialButton
        onPress={
          touchModeEnabled ? openTagsModal : () => handleAddTaggedMaterial()
        }
        icon={<LucideAsterisk />}
      >
        {t.loadStaged.addTaggedMaterial}
      </MaterialButton>

      <MaterialButton onPress={handleOpenCatalyticModal}>
        {t.loadStaged.addCatalyticConverters}
      </MaterialButton>

      <InventoryItemsModal
        isOpen={materialsModalState.isOpen}
        mode={materialsModalState.itemType}
        onOpenChange={handleModalOpenChange}
        onSelectMaterial={handleMaterialSelect}
        onSelectInventoryTag={handleTagSelect}
        modalStyle={modalStyle}
        initialSelectedItemId={materialsModalState.selectedItemId}
        highlightedMaterialIds={highlightedMaterialIds}
        highlightedSectionTitle={highlightedSectionTitle}
      />

      <CatalyticConverterModal
        isOpen={catalyticModalState.isOpen}
        mode={catalyticModalState.isOpen ? catalyticModalState.mode : 'add'}
        initialSelectedItemId={
          catalyticModalState.isOpen ? catalyticModalState.selectedItemId : null
        }
        lineItemId={
          catalyticModalState.isOpen ? catalyticModalState.lineItemId : null
        }
        touchModeEnabled={touchModeEnabled}
        onOpenChange={(isOpen) => {
          if (!isOpen) closeCatalyticModal();
        }}
        onSelectItem={handleCatalyticItemSelect}
        modalStyle={modalStyle}
      />
    </>
  );
};
